'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { StrapiImage } from '@/components/StrapiImage'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type Props = {
  images: string[]
  alt: string
  className?: string
  /** Limit the number of images rendered in slider. */
  maxImages?: number
  /** Slightly larger slider dots (used in quick preview). */
  largeDots?: boolean
  /** Tailwind aspect class for the media area (default 4:3). */
  aspectClassName?: string
  /**
   * Fill parent height instead of aspect ratio (list rows). Parent chain should
   * establish height (e.g. flex stretch). Merge `className` for breakpoints
   * (e.g. `lg:aspect-[16/9] lg:h-auto`) when you need desktop to use aspect again.
   */
  fillHeight?: boolean
}

/**
 * Horizontal swipe carousel (touch / trackpad scroll) with dot indicators only.
 */
export function ServiceImageCarousel({
  images,
  alt,
  className,
  maxImages,
  largeDots = false,
  aspectClassName = 'aspect-[4/3]',
  fillHeight = false,
}: Props) {
  const visibleImages =
    typeof maxImages === 'number' && maxImages > 0
      ? images.slice(0, maxImages)
      : images
  const scrollRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  const scrollTo = useCallback((i: number) => {
    const el = scrollRef.current
    if (!el) return
    const w = el.clientWidth
    if (w <= 0) return
    el.scrollTo({ left: i * w, behavior: 'smooth' })
  }, [])

  const goPrev = useCallback(() => {
    if (visibleImages.length <= 1) return
    const next = index === 0 ? visibleImages.length - 1 : index - 1
    scrollTo(next)
  }, [visibleImages.length, index, scrollTo])

  const goNext = useCallback(() => {
    if (visibleImages.length <= 1) return
    const next = index === visibleImages.length - 1 ? 0 : index + 1
    scrollTo(next)
  }, [visibleImages.length, index, scrollTo])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || visibleImages.length <= 1) return

    const onScroll = () => {
      const w = el.clientWidth
      if (w <= 0) return
      const i = Math.round(el.scrollLeft / w)
      setIndex(Math.min(Math.max(0, i), visibleImages.length - 1))
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => el.removeEventListener('scroll', onScroll)
  }, [visibleImages.length])

  useEffect(() => {
    setIndex((prev) => Math.min(prev, Math.max(visibleImages.length - 1, 0)))
  }, [visibleImages.length])

  const frameClass = fillHeight
    ? 'relative h-full min-h-0 w-full min-w-0 bg-muted'
    : cn('relative w-full bg-muted', aspectClassName)

  if (visibleImages.length === 0) {
    return (
      <div
        className={cn(
          frameClass,
          fillHeight && 'min-h-[100px]',
          className
        )}
      >
        <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
          Nema slike
        </div>
      </div>
    )
  }

  if (visibleImages.length === 1) {
    return (
      <div
        className={cn(
          fillHeight
            ? cn(
                'relative h-full min-h-0 w-full min-w-0 overflow-hidden bg-muted',
                className
              )
            : cn('relative w-full overflow-hidden bg-muted', aspectClassName, className)
        )}
      >
        <StrapiImage
          src={visibleImages[0]}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
          className="object-cover"
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        fillHeight
          ? cn(
              'relative h-full min-h-0 w-full min-w-0 bg-muted',
              className
            )
          : cn('relative w-full', aspectClassName, className)
      )}
    >
      <div
        ref={scrollRef}
        className={cn(
          'absolute inset-0 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain',
          'touch-pan-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          'bg-muted'
        )}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {visibleImages.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="relative h-full min-h-0 w-full min-w-full shrink-0 snap-center snap-always"
          >
            <StrapiImage
              src={src}
              alt={i === 0 ? alt : ''}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              className="object-cover"
              draggable={false}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        aria-label="Prethodna slika"
        className="absolute left-2 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/60 md:flex"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          goPrev()
        }}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <button
        type="button"
        aria-label="Sledeća slika"
        className="absolute right-2 top-1/2 z-20 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/60 md:flex"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          goNext()
        }}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div
        className="pointer-events-none absolute bottom-2 left-0 right-0 z-10 flex justify-center gap-1.5"
        role="tablist"
        aria-label="Slike"
      >
        {visibleImages.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Slika ${i + 1} od ${visibleImages.length}`}
            tabIndex={i === index ? 0 : -1}
            className={cn(
              'pointer-events-auto rounded-full border-0 p-0 shadow-sm transition-colors',
              largeDots ? 'h-2.5 w-2.5' : 'h-1.5 w-1.5',
              i === index
                ? 'bg-white'
                : 'bg-white/70 hover:bg-white/90'
            )}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              scrollTo(i)
            }}
          />
        ))}
      </div>
    </div>
  )
}
